import React, { useState, useEffect } from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, IconButton, ListItemProps, Collapse, Menu, MenuItem, Divider, Avatar, Typography, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { HomeIcon, TrainingIcon, ToolkitIcon, ReportsIcon, AdminIcon } from '../icons/NavigationIcons';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import { Logo } from './Logo';
import { useAuth } from '../../contexts/AuthContext';
import { courses, flows } from '../../services/api';
import { FlowCompletionStatus, FlowRequirementsMet } from '../../types';

const StyledSidebar = styled(Box)(({ theme }) => ({
  width: '280px',
  height: '100vh',
  backgroundColor: '#000000',
  display: 'flex',
  flexDirection: 'column',
  position: 'fixed',
  left: 0,
  top: 0,
  borderRight: '1px solid rgba(255, 255, 255, 0.1)',
  zIndex: 1200,
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(3),
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '3px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.3)',
    },
  },
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  flexShrink: 0,
  '& img': {
    height: '120px',
    width: 'auto',
  }
});

interface StyledListItemProps extends ListItemProps {
  active?: boolean;
}

const StyledListItem = styled(ListItem)<StyledListItemProps>(({ theme, active }) => ({
  borderRadius: '15px',
  marginBottom: theme.spacing(1),
  backgroundColor: active ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
  cursor: 'pointer',
  padding: theme.spacing(1.5, 2),
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

interface SubMenuItemProps extends ListItemProps {
  active?: boolean;
}

const SubMenuItem = styled(ListItem)<SubMenuItemProps>(({ theme, active }) => ({
  paddingLeft: theme.spacing(4),
  borderRadius: '15px',
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  backgroundColor: active ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const navigationItems = [
  { text: 'Home', icon: HomeIcon, path: '/home' },
  { 
    text: 'Lifeplan', 
    icon: TrainingIcon, 
    path: '/training',
    hasFlows: true
  },
  { text: 'Toolkit', icon: ToolkitIcon, path: '/toolkit' },
  { text: 'Reports', icon: ReportsIcon, path: '/reports' },
];

const adminItems = [
  { text: 'Flow Management', path: '/admin/flows' },
  { text: 'Admin Reports', path: '/admin/reports' },
  { text: 'Course Management', path: '/admin/courses' },
  { text: 'Content Import', path: '/admin/content-import' },
  { text: 'Intake Assessment', path: '/flows/intake_assessment_v0' },
  { text: 'Mock Quiz Test', path: '/flows/mock_quiz_test' },
  { text: 'Test Quiz Results', path: '/quiz-results/1' },
];

const TopSection = styled(Box)({
  marginBottom: '40px',
});

const BottomSection = styled(Box)({
  marginTop: 'auto',
});

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lifePlanOpen, setLifePlanOpen] = useState(false);
  const [lifePlanFlows, setLifePlanFlows] = useState<any[]>([]);
  const [flowStatus, setFlowStatus] = useState<Record<string, { completed: boolean, requirementsMet: boolean }>>({});
  const menuButtonRef = React.useRef<HTMLLIElement>(null);

  useEffect(() => {
    const fetchLifePlanFlows = async () => {
      try {
        const flowsResponse = await courses.getFlows(4);
        console.log('Flows data:', flowsResponse.data);
        
        if (Array.isArray(flowsResponse.data)) {
          setLifePlanFlows(flowsResponse.data);
          
          const flowsData = flowsResponse.data;
          const statusMap: Record<string, { completed: boolean, requirementsMet: boolean }> = {};
          
          for (const flowItem of flowsData) {
            const flowDetails = flowItem.flow_details || {};
            const flowName = flowDetails.name || '';
            
            if (flowName) {
              try {
                const completionStatus = await flows.getCompletionStatus(flowName);
                const requirementsStatus = await flows.checkRequirementsMet(flowName);
                
                statusMap[flowName] = {
                  completed: !!completionStatus.completed_at,
                  requirementsMet: requirementsStatus.requirements_met
                };
              } catch (error) {
                console.error(`Error fetching status for flow ${flowName}:`, error);
                statusMap[flowName] = { completed: false, requirementsMet: true };
              }
            }
          }
          
          setFlowStatus(statusMap);
        } else {
          console.error('Unexpected data format:', flowsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching lifeplan flows:', error);
      }
    };

    fetchLifePlanFlows();
  }, []);

  const handleAdminClick = () => {
    setAdminOpen(!adminOpen);
  };

  const handleUserMenuClick = () => {
    setMenuOpen(true);
  };

  const handleUserMenuClose = () => {
    setMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/login');
  };

  const getInitials = () => {
    if (!user?.username) return '?';
    return user.username.charAt(0).toUpperCase();
  };

  return (
    <StyledSidebar>
      <LogoContainer>
        <Logo />
      </LogoContainer>
      <ScrollableContent>
        <TopSection>
          <List>
            {navigationItems.map((item) => (
              <React.Fragment key={item.text}>
                <StyledListItem
                  active={location.pathname === item.path || (item.hasFlows && location.pathname.startsWith('/flows/'))}
                  onClick={item.hasFlows ? () => setLifePlanOpen(!lifePlanOpen) : () => navigate(item.path)}
                >
                  <ListItemIcon sx={{ color: 'text.primary', minWidth: 40 }}>
                    <item.icon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontFamily: 'Montserrat',
                        fontWeight: 500,
                        fontSize: '1rem',
                      }
                    }}
                  />
                  {item.hasFlows && (lifePlanOpen ? <ExpandLess /> : <ExpandMore />)}
                </StyledListItem>
                {item.hasFlows && (
                  <Collapse in={lifePlanOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {lifePlanFlows.map((flowItem) => {
                        const flowDetails = flowItem.flow_details || {};
                        const flowName = flowDetails.name || '';
                        
                        let flowTitle = flowDetails.description || flowName || 'Unnamed Flow';
                        if (flowTitle.endsWith(' Flow')) {
                          flowTitle = flowTitle.slice(0, -5);
                        }
                        
                        if (!flowName) {
                          return null;
                        }
                        
                        return (
                          <SubMenuItem
                            key={flowItem.id}
                            onClick={() => {
                              const status = flowStatus[flowName] || { completed: false, requirementsMet: true };
                              if (status.requirementsMet) {
                                navigate(`/flows/${flowName}`);
                              }
                            }}
                            active={location.pathname === `/flows/${flowName}`}
                            sx={{
                              pl: 4,
                              opacity: flowStatus[flowName]?.requirementsMet ? 1 : 0.6,
                              cursor: flowStatus[flowName]?.requirementsMet ? 'pointer' : 'not-allowed',
                            }}
                          >
                            <ListItemText
                              primary={flowTitle}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontFamily: 'Montserrat',
                                  fontWeight: 500,
                                  fontSize: '0.85rem',
                                }
                              }}
                            />
                            {flowStatus[flowName]?.completed ? (
                              <Tooltip title="Completed">
                                <CheckCircleIcon color="success" fontSize="small" />
                              </Tooltip>
                            ) : flowStatus[flowName]?.requirementsMet ? (
                              <Tooltip title="Available">
                                <CircleIcon color="primary" fontSize="small" />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Locked - Complete previous flows first">
                                <LockIcon color="disabled" fontSize="small" />
                              </Tooltip>
                            )}
                          </SubMenuItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            ))}

            {user?.is_staff && (
              <>
                <StyledListItem onClick={handleAdminClick}>
                  <ListItemIcon sx={{ color: 'text.primary', minWidth: 40 }}>
                    <AdminIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Admin"
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontFamily: 'Montserrat',
                        fontWeight: 500,
                        fontSize: '1rem',
                      }
                    }}
                  />
                  {adminOpen ? <ExpandLess /> : <ExpandMore />}
                </StyledListItem>
                <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {adminItems.map((item) => (
                      <SubMenuItem
                        key={item.text}
                        onClick={() => navigate(item.path)}
                        active={location.pathname === item.path}
                      >
                        <ListItemText
                          primary={item.text}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontFamily: 'Montserrat',
                              fontWeight: 500,
                              fontSize: '0.9rem',
                            }
                          }}
                        />
                      </SubMenuItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </List>
        </TopSection>

        <BottomSection>
          <List>
            <StyledListItem onClick={() => navigate('/help')}>
              <ListItemIcon sx={{ color: 'text.primary', minWidth: 40 }}>
                <HelpOutlineIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Help"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontFamily: 'Montserrat',
                    fontWeight: 500,
                    fontSize: '1rem',
                  }
                }}
              />
            </StyledListItem>
            <StyledListItem onClick={handleUserMenuClick} ref={menuButtonRef}>
              <ListItemIcon sx={{ color: 'text.primary', minWidth: 40 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  {getInitials()}
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary={user?.username || 'Profile'}
                secondary="View profile"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontFamily: 'Montserrat',
                    fontWeight: 500,
                    fontSize: '1rem',
                  },
                  '& .MuiListItemText-secondary': {
                    fontSize: '0.75rem',
                    opacity: 0.7,
                  }
                }}
              />
              <ExpandMore />
            </StyledListItem>
          </List>

          <Menu
            anchorEl={menuButtonRef.current}
            open={menuOpen}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: 'rgba(38, 38, 38, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                minWidth: 200,
                marginLeft: 1,
              }
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Signed in as
              </Typography>
              <Typography variant="body1" color="text.primary">
                {user?.username || 'User'}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            <MenuItem onClick={() => { navigate('/profile'); handleUserMenuClose(); }}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { navigate('/settings'); handleUserMenuClose(); }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              Sign Out
            </MenuItem>
          </Menu>
        </BottomSection>
      </ScrollableContent>
    </StyledSidebar>
  );
}; 